/* eslint-disable react/prop-types */
/**
 * ImpersonationBanner — sticky top bar shown when staff are signed in
 * AS a customer's portal owner (Iter 232).
 *
 * Reads localStorage keys set by `CustomerDetail.PortalPreviewButton`:
 *   hv_impersonator_token — the staff JWT to restore on exit
 *   hv_impersonator_user  — { id, name, email, role, return_to, customer_name }
 *
 * Provides a single "Return to staff session" action that swaps the
 * tokens back, resets the AuthContext user, and navigates the staff
 * back to where they were.
 *
 * Trust Contract: lives inline at the very top of the document, NO modal.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, LogOut } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

function readImpersonator() {
  try {
    const token = localStorage.getItem("hv_impersonator_token");
    const rawUser = localStorage.getItem("hv_impersonator_user");
    if (!token || !rawUser) return null;
    const user = JSON.parse(rawUser);
    return { token, user };
  } catch {
    return null;
  }
}

export default function ImpersonationBanner() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [imp, setImp] = useState(() => readImpersonator());
  const [busy, setBusy] = useState(false);

  // Re-check on route/storage change so the banner appears the moment
  // a staff member impersonates and disappears on return.
  useEffect(() => {
    const refresh = () => setImp(readImpersonator());
    window.addEventListener("storage", refresh);
    window.addEventListener("hv:impersonation-changed", refresh);
    return () => {
      window.removeEventListener("storage", refresh);
      window.removeEventListener("hv:impersonation-changed", refresh);
    };
  }, []);

  // Also poll once after mount to pick up the localStorage write that
  // happened in the same tab (the `storage` event only fires cross-tab).
  useEffect(() => {
    const t = setTimeout(() => setImp(readImpersonator()), 50);
    return () => clearTimeout(t);
  }, []);

  if (!imp) return null;

  const exit = async () => {
    setBusy(true);
    try {
      // Swap tokens back BEFORE fetching the staff /auth/me — otherwise
      // /auth/me would still return the impersonated client.
      localStorage.setItem("hv_token", imp.token);
      localStorage.removeItem("hv_impersonator_token");
      localStorage.removeItem("hv_impersonator_user");
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
      } catch {
        // Token might have expired during impersonation — force login.
        setUser(false);
        localStorage.removeItem("hv_token");
        navigate("/login", { replace: true });
        return;
      }
      window.dispatchEvent(new CustomEvent("hv:impersonation-changed"));
      navigate(imp.user?.return_to || "/", { replace: true });
    } finally {
      setBusy(false);
    }
  };

  const customerName = imp.user?.customer_name || "this customer";

  return (
    <div
      data-testid="impersonation-banner"
      className="sticky top-0 z-[200] bg-amber-500/95 text-[#0a0b0e] border-b border-amber-700 backdrop-blur-md"
      style={{ paddingTop: "env(safe-area-inset-top)" }}
    >
      <div className="max-w-7xl mx-auto px-3 sm:px-6 py-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs sm:text-sm font-medium">
        <AlertTriangle className="w-4 h-4 shrink-0" />
        <span className="min-w-0">
          Acting as{" "}
          <span className="font-bold underline decoration-dotted">
            {customerName}
          </span>{" "}
          portal owner — every action is audit-logged.
        </span>
        <span className="hidden md:inline text-[#0a0b0e]/70">
          (Returning to {imp.user?.name || imp.user?.email || "your staff session"})
        </span>
        <button
          type="button"
          onClick={exit}
          disabled={busy}
          data-testid="impersonation-exit-btn"
          className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-[#0a0b0e] text-white px-3 py-1 text-xs font-bold hover:bg-[#1a1b1f] transition disabled:opacity-50"
        >
          <LogOut className="w-3.5 h-3.5" />
          {busy ? "Returning…" : "Return to staff session"}
        </button>
      </div>
    </div>
  );
}
